import React from 'react';
import {
  BookOpen,
  Briefcase,
  HeartPulse,
  Microscope,
  Newspaper,
  Pill,
  ShieldCheck,
  Target,
  Users,
  UserRound,
} from 'lucide-react';

const HERO_PLACEHOLDER =
  'https://medintel.net/doc.png';

const coverageCards = [
  {
    title: 'Medical News & Healthcare Updates',
    description: 'Timely, accurate and relevant updates from around the world.',
    icon: Newspaper,
    iconWrap: 'bg-[#e8f1fb]',
    iconColor: 'text-[#2563eb]',
  },
  {
    title: 'Conference Highlights',
    description: 'Key takeaways from major global medical conferences.',
    icon: Users,
    iconWrap: 'bg-[#e6f7f5]',
    iconColor: 'text-[#0d9488]',
  },
  {
    title: 'Expert Insights',
    description: 'Interviews and opinions from leading experts and key opinion leaders.',
    icon: UserRound,
    iconWrap: 'bg-[#f0ebf8]',
    iconColor: 'text-[#7c3aed]',
  },
  {
    title: 'Clinical Research & Evidence',
    description: 'Summaries of impactful studies and emerging evidence.',
    icon: Microscope,
    iconWrap: 'bg-[#e6f4f8]',
    iconColor: 'text-[#0891b2]',
  },
  {
    title: 'Therapeutics & Drug Updates',
    description: 'Approvals, new therapies and important safety information.',
    icon: Pill,
    iconWrap: 'bg-[#e8f8ef]',
    iconColor: 'text-[#059669]',
  },
  {
    title: 'Medical Education & Resources',
    description: 'Learning resources and educational content to support continuous growth.',
    icon: BookOpen,
    iconWrap: 'bg-[#fef3e8]',
    iconColor: 'text-[#d97706]',
  },
  {
    title: 'Practice Management & Innovation',
    description: 'Solutions and innovations to enhance clinical practice and efficiency.',
    icon: Briefcase,
    iconWrap: 'bg-[#e8f1fb]',
    iconColor: 'text-[#2563eb]',
  },
];

const missionPillars = [
  { label: 'Trusted Information', icon: ShieldCheck },
  { label: 'Practical Insights', icon: Target },
  { label: 'Better Healthcare', icon: HeartPulse },
];

function DnaHelix() {
  return (
    <svg
      className="h-full w-full"
      viewBox="0 0 320 520"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <defs>
        <linearGradient id="dnaGlow" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#48B7FF" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#1177FE" stopOpacity="0.35" />
        </linearGradient>
        <filter id="dnaBlur">
          <feGaussianBlur stdDeviation="6" />
        </filter>
      </defs>
      <g opacity="0.85">
        <path
          d="M160 20 C210 90 110 150 160 220 C210 290 110 350 160 420 C210 470 110 500 160 500"
          stroke="url(#dnaGlow)"
          strokeWidth="4"
          fill="none"
        />
        <path
          d="M160 20 C110 90 210 150 160 220 C110 290 210 350 160 420 C110 470 210 500 160 500"
          stroke="url(#dnaGlow)"
          strokeWidth="4"
          fill="none"
        />
        {[60, 100, 140, 180, 220, 260, 300, 340, 380, 420, 460].map((y) => (
          <line key={y} x1="110" y1={y} x2="210" y2={y} stroke="#48B7FF" strokeOpacity="0.55" strokeWidth="2" />
        ))}
      </g>
      <ellipse cx="200" cy="260" rx="80" ry="120" fill="#48B7FF" opacity="0.12" filter="url(#dnaBlur)" />
    </svg>
  );
}

const About = () => {
  return (
    <main className="font-sans text-slate-600">
      {/* Hero */}
      <section className="relative bg-white" aria-labelledby="about-hero-heading">
        <div className="grid lg:grid-cols-2 lg:items-stretch">
          {/* Left: copy */}
          <div className="relative z-0 flex flex-col justify-center bg-[#f4f5f7] px-8 pb-28 pt-14 sm:px-12 sm:pb-32 sm:pt-16 lg:px-16 lg:pb-36 lg:pt-20 xl:px-24">
            <p className="text-[12px] font-semibold uppercase tracking-[0.18em] text-[#1a2332]">
              ABOUT US
            </p>
            <span className="mt-2 block h-[2px] w-9 bg-[#14b8a6]" aria-hidden />

            <h1
              id="about-hero-heading"
              className="mt-5 max-w-[420px] font-playfair text-[2rem] font-bold leading-[1.22] text-[#0b1f3a] sm:text-[2.25rem] lg:text-[2.5rem]"
            >
              Empowering Healthcare Professionals with Trusted Knowledge
            </h1>

            <span className="mt-5 block h-[2px] w-9 bg-[#14b8a6]" aria-hidden />

            <p className="mt-5 max-w-[440px] text-[15px] leading-[1.75] text-[#3d4a5c]">
              We are an independent healthcare knowledge and medical media platform dedicated to
              delivering trusted, evidence-based information for healthcare professionals.
            </p>
            <p className="mt-4 max-w-[440px] text-[15px] leading-[1.75] text-[#3d4a5c]">
              Our mission is to bridge the gap between scientific evidence and clinical practice by
              providing timely, relevant, and credible insights that support informed decisions and
              improve patient care.
            </p>
          </div>

          {/* Right: image */}
          <div className="relative min-h-[340px] sm:min-h-[420px] lg:min-h-[520px]">
            <img
              src={HERO_PLACEHOLDER}
              alt="Healthcare professional reviewing information on a tablet"
              className="absolute inset-0 h-full w-full object-cover object-[center_25%]"
              loading="eager"
              width={1400}
              height={900}
            />
          </div>
        </div>

        {/* Commitment card — straddles text / image boundary like the mockup */}
        <div className="pointer-events-none absolute inset-x-0 bottom-8 z-20 px-8 sm:bottom-10 sm:px-12 lg:bottom-12 lg:px-0">
          <div className="pointer-events-auto mx-auto w-full max-w-[400px] rounded-md bg-white px-5 py-5 shadow-[0_10px_40px_rgba(11,31,58,0.14)] sm:px-6 sm:py-6 lg:mx-0 lg:ml-[calc(50%-200px)]">
            <div className="flex items-start gap-4">
              <svg
                className="mt-0.5 h-8 w-8 shrink-0"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden
              >
                <path
                  d="M12 2.5 4.5 5.5v5.2c0 5.1 3.3 9.7 7.5 11 4.2-1.3 7.5-5.9 7.5-11V5.5L12 2.5Z"
                  fill="#14b8a6"
                />
                <path
                  d="M9.2 12.1 11 14l3.8-4.2"
                  stroke="#fff"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <div>
                <h2 className="font-playfair text-[17px] font-bold leading-snug text-[#0b1f3a] sm:text-lg">
                  Our Commitment
                </h2>
                <p className="mt-1.5 text-[13px] leading-[1.65] text-[#4b5563]">
                  Editorial integrity, scientific accuracy, and unbiased reporting are the foundation of
                  everything we publish.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What We Cover */}
      <section className="bg-white py-16 sm:py-20 lg:py-24" aria-labelledby="about-coverage-heading">
        <div className="mx-auto max-w-[1280px] px-6 sm:px-10 lg:px-12">
          <div className="mx-auto max-w-3xl text-center">
            <span className="mx-auto block h-[3px] w-14 bg-teal-accent" aria-hidden />
            <h2
              id="about-coverage-heading"
              className="mt-5 font-playfair text-3xl font-bold text-navy-900 sm:text-[2.1rem]"
            >
              What We Cover
            </h2>
            <p className="mx-auto mt-5 max-w-3xl text-[15px] leading-relaxed text-slate-500 sm:text-base">
              We bring together important developments from leading medical journals, global scientific
              conferences, regulatory agencies, and expert voices across the healthcare ecosystem.
            </p>
          </div>

          <div className="mt-14 space-y-5">
            <ul className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {coverageCards.slice(0, 4).map(({ title, description, icon: Icon, iconWrap, iconColor }) => (
                <li
                  key={title}
                  className="rounded-xl border border-slate-100 bg-white p-6 shadow-card transition-shadow hover:shadow-card-hover"
                >
                  <div
                    className={`mb-5 inline-flex h-11 w-11 items-center justify-center rounded-lg ${iconWrap}`}
                  >
                    <Icon className={`h-[22px] w-[22px] ${iconColor}`} strokeWidth={1.75} aria-hidden />
                  </div>
                  <h3 className="text-[15px] font-semibold leading-snug text-navy-900">{title}</h3>
                  <p className="mt-2.5 text-[13px] leading-relaxed text-slate-500">{description}</p>
                </li>
              ))}
            </ul>

            <ul className="mx-auto grid max-w-[960px] grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {coverageCards.slice(4).map(({ title, description, icon: Icon, iconWrap, iconColor }) => (
                <li
                  key={title}
                  className="rounded-xl border border-slate-100 bg-white p-6 shadow-card transition-shadow hover:shadow-card-hover"
                >
                  <div
                    className={`mb-5 inline-flex h-11 w-11 items-center justify-center rounded-lg ${iconWrap}`}
                  >
                    <Icon className={`h-[22px] w-[22px] ${iconColor}`} strokeWidth={1.75} aria-hidden />
                  </div>
                  <h3 className="text-[15px] font-semibold leading-snug text-navy-900">{title}</h3>
                  <p className="mt-2.5 text-[13px] leading-relaxed text-slate-500">{description}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Our Mission */}
      <section
        className="relative overflow-hidden bg-navy-900 py-16 text-white sm:py-20 lg:py-24"
        aria-labelledby="about-mission-heading"
      >
        <div className="pointer-events-none absolute inset-y-0 left-0 w-1/3 opacity-40" aria-hidden>
          <div className="absolute left-8 top-1/2 h-48 w-48 -translate-y-1/2 rounded-full border border-white/10" />
          <div className="absolute left-20 top-1/3 h-32 w-32 rounded-full border border-white/5" />
        </div>

        <div
          className="pointer-events-none absolute inset-y-0 right-0 w-[45%] max-w-xl opacity-90"
          aria-hidden
        >
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_75%_50%,rgba(72,183,255,0.22),transparent_65%)]" />
          <div className="absolute inset-y-0 right-0 w-full translate-x-8">
            <DnaHelix />
          </div>
        </div>

        <div className="relative mx-auto max-w-[1280px] px-6 sm:px-10 lg:px-12">
          <div className="flex flex-col items-center gap-10 lg:flex-row lg:items-center lg:gap-14">
            {/* Concentric-ring emblem */}
            <div className="relative flex h-44 w-44 shrink-0 items-center justify-center sm:h-52 sm:w-52" aria-hidden>
              <span className="absolute inset-0 rounded-full border border-dashed border-white/20" />
              <span className="absolute inset-4 rounded-full border border-white/10" />
              <span className="absolute inset-8 rounded-full border-2 border-white/15 bg-white/[0.03]" />
              <Users className="relative h-16 w-16 text-teal-300 sm:h-20 sm:w-20" strokeWidth={1.25} />
            </div>

            <div className="max-w-2xl text-center lg:text-left">
              <h2 id="about-mission-heading" className="font-playfair text-3xl font-bold sm:text-[2.1rem]">
                Our Mission
              </h2>
              <p className="mt-4 text-[15px] leading-[1.75] text-slate-300 sm:text-base">
                To simplify complex medical information into meaningful insights that empower healthcare
                professionals to stay informed, make better decisions, and deliver the best possible care.
              </p>

              <ul className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-5 lg:justify-start">
                {missionPillars.map(({ label, icon: Icon }, index) => (
                  <li key={label} className="flex items-center">
                    {index > 0 && (
                      <span className="mr-6 hidden h-10 w-px bg-white/20 sm:block" aria-hidden />
                    )}
                    <span className="flex items-center gap-3">
                      <Icon className="h-7 w-7 shrink-0 text-white" strokeWidth={1.5} aria-hidden />
                      <span className="text-left text-[15px] font-medium leading-tight text-slate-100">
                        {label.split(' ').map((word) => (
                          <span key={word} className="block">
                            {word}
                          </span>
                        ))}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Closing */}
      <section
        className="bg-[#eef5fa] py-16 sm:py-20 lg:py-24"
        aria-labelledby="about-closing-heading"
      >
        <div className="mx-auto max-w-4xl px-6 text-center sm:px-10">
          <h2 id="about-closing-heading" className="sr-only">
            Our promise
          </h2>
          <p className="font-playfair text-xl font-bold leading-relaxed text-navy-900 sm:text-2xl lg:text-[1.75rem] lg:leading-snug">
            As medicine evolves, we remain committed to being a reliable partner in your journey of
            lifelong learning and clinical excellence.
          </p>
          <span className="mx-auto mt-10 block h-[3px] w-14 bg-teal-accent" aria-hidden />
          <p className="mt-6 font-playfair text-2xl font-bold text-teal-accent sm:text-[1.85rem]">
            Trusted Knowledge. Practical Insights. Better Healthcare.
          </p>
        </div>
      </section>
    </main>
  );
};

export default About;
